import React, { useState, useEffect } from 'react';
import { Bell, BellRing, X, Sparkles, Check, ChevronRight, Mail, Loader2, BookOpen } from 'lucide-react';
import { useWebPush } from '../hooks/useWebPush';
import { useAuth } from '../context/AuthContext';

export const WebPushBanner: React.FC = () => {
  const {
    isSupported,
    isSubscribed,
    isLoading,
    actionLoading,
    subscribe,
    toastMessage,
    error,
    clearError,
    clearToast,
  } = useWebPush();

  const { currentUser, subscribeAndSignInWithEmail } = useAuth();
  const [emailInput, setEmailInput] = useState('');
  const [showEmailField, setShowEmailField] = useState(false);

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('literafan_push_banner_dismissed') === 'true';
    }
    return false;
  });

  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!isSupported || isSubscribed || dismissed || isLoading) {
      setVisible(false);
      return;
    }

    // Aguarda 3.5 segundos para não sobrecarregar o usuário na entrada
    const timer = setTimeout(() => {
      setVisible(true);
    }, 3500);

    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, dismissed, isLoading]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('literafan_push_banner_dismissed', 'true');
  };

  const handleSubscribe = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    const emailToUse = emailInput.trim() || currentUser?.email || undefined;
    
    // Se o usuário digitou e-mail, também cadastra na newsletter
    if (emailToUse && emailToUse.includes('@')) {
      try {
        await subscribeAndSignInWithEmail(emailToUse).catch(() => {});
        await fetch('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailToUse, source: 'push_banner' })
        }).catch(() => {});
      } catch (err) {
        console.warn('Erro ao sincronizar newsletter:', err);
      }
    }

    const success = await subscribe(['articles', 'chapters', 'all'], emailToUse);
    if (success) {
      setTimeout(() => {
        setVisible(false);
      }, 3500);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 max-w-sm w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-5 duration-300 font-sans">
      <div className="p-4 sm:p-5 rounded-2xl bg-[#121212]/95 backdrop-blur-xl border border-primary/40 shadow-[0_12px_40px_rgba(0,0,0,0.8),0_0_20px_rgba(193,18,31,0.2)] relative overflow-hidden">
        {/* Glow de fundo */}
        <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-primary/15 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-28 h-28 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-red-500 to-rose-600 animate-pulse" />

        {/* Botão de Fechar */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-stone-400 hover:text-stone-200 p-1 rounded-lg hover:bg-stone-800 transition"
          aria-label="Fechar aviso"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start space-x-3.5 pr-6">
          <div className="p-2.5 rounded-xl bg-primary/15 border border-primary/30 text-primary shrink-0 mt-0.5 shadow-[0_0_12px_rgba(193,18,31,0.25)]">
            <BellRing className="w-5 h-5 animate-pulse" />
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-primary tracking-wider uppercase font-sans">
                Novos Artigos
              </span>
              <span className="text-[10px] bg-stone-800 text-stone-300 px-1.5 py-0.5 rounded border border-stone-700">
                Newsletter & Alertas
              </span>
            </div>

            <h4 className="text-sm font-semibold text-stone-100 font-serif leading-snug">
              Seja avisado a cada novo artigo e capítulo publicado
            </h4>

            <p className="text-xs text-stone-400 leading-relaxed pt-0.5">
              Receba notificações direto no celular ou desktop e fique por dentro de todos os lançamentos do Literafan.
            </p>
          </div>
        </div>

        {/* Campo opcional de e-mail para atrelar à Newsletter */}
        <form onSubmit={handleSubscribe} className="mt-3.5 space-y-2.5">
          {!currentUser?.isNewsletterSubscriber && (
            <div className="relative">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Seu e-mail (para receber na newsletter)"
                className="w-full px-3.5 py-2 pl-9 bg-stone-900 border border-stone-700/80 rounded-xl text-xs text-stone-200 placeholder-stone-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
              <Mail className="w-3.5 h-3.5 text-stone-500 absolute left-3 top-2.5" />
            </div>
          )}

          {/* Mensagens de feedback */}
          {toastMessage && (
            <div className="p-2 rounded-lg bg-emerald-950/60 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 shrink-0" />
              <span>{toastMessage}</span>
            </div>
          )}

          {error && (
            <div className="p-2 rounded-lg bg-rose-950/60 border border-rose-500/40 text-rose-300 text-xs">
              {error}
            </div>
          )}

          {/* Botões de Ação */}
          <div className="pt-2 border-t border-stone-800 flex items-center justify-between">
            <button
              type="button"
              onClick={handleDismiss}
              className="px-2.5 py-1.5 text-xs text-stone-400 hover:text-stone-200 font-medium transition"
            >
              Agora não
            </button>

            <button
              type="submit"
              disabled={actionLoading}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary via-red-600 to-rose-700 hover:from-red-600 hover:to-primary text-white font-bold text-xs tracking-wide transition shadow-lg shadow-primary/25 flex items-center gap-1.5 disabled:opacity-50 active:scale-95"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Ativando...</span>
                </>
              ) : (
                <>
                  <Bell className="w-3.5 h-3.5" />
                  <span>Ativar Notificações</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebPushBanner;
